import { clsx } from 'clsx';
import * as React from 'react';

import { type BlockTableData, table } from '@gitbook/doc-core';
import { usePaletteHandle } from '@gitbook/react-uikit';
import type { TextRange } from '@gitbook/slate';
import { type BlockProps, ToggleFragmentEditing } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { PlaceholderButton, Tooltip } from '@gitbook/spine-react';
import { List } from '@gitbook/util-immutable';
import { filterOutNullable } from '@gitbook/util-ts';

import { shortcuts } from '../../../Keyboard';
import {
    BlockLoadingFallback,
    BlockWrapper,
    useBlockLayoutGroupFactory,
    VoidBlockFrame,
} from '../common';
import { createColumnDefsPalette } from './ColumnDefinitions';
import { TableStatesContext } from './contexts';
import { useTableLayout } from './hooks/useTableLayout';
import { useTablePaletteFactory } from './palette';
import { RecordModal } from './RecordModal';
import type { TableState } from './types';
import { computeRecordsSubview } from './utils';
import { viewSpecs } from './Views';

/*
 * Render a Table block.
 */
export function BlockTable(props: BlockProps): React.ReactElement {
    const { node, editor, isFocused, selections } = props;
    const tableData = node.data.toFrozenObject() as BlockTableData;
    const readOnly = editor.readOnly;

    /** Ordered set of the records to display */
    const records = React.useMemo(() => computeRecordsSubview(tableData), [tableData]);

    /** Record to open in a modal */
    const [openedRecordKey, setOpenedRecordKey] = React.useState<string | null>(null);
    const openedRecord = React.useMemo(() => {
        if (openedRecordKey === null) {
            return null;
        }

        return records.find((record) => record.key === openedRecordKey);
    }, [records, openedRecordKey]);

    const openRecord = React.useCallback(
        (recordId: string) => {
            setOpenedRecordKey(recordId);
        },
        [setOpenedRecordKey]
    );

    const otherSelections = React.useMemo(() => {
        const states: table.TableSelection[] = [];
        const filtered: TextRange[] = [];

        selections.forEach((selection) => {
            const state = table.getTableSelection(editor.value.document, selection, node);
            states.push(state);

            // Only highlight the entire block if the selection is not specific to a cell/row
            if (!state.recordId && !state.definitionId) {
                filtered.push(selection);
            }
        });

        return {
            states,
            filtered: List.create(filtered),
        };
    }, [node, selections, editor.value.document]);

    /** Selection in the table */
    const selection = React.useMemo<table.TableSelection>(() => {
        if (readOnly) {
            return {};
        }

        return table.getTableSelection(editor.value.document, editor.value.selection, node);
    }, [node, readOnly, editor.value.selection, editor.value.document]);

    const isTableBlockFocused = isFocused && !selection.definitionId && !selection.recordId;

    const tableState: TableState = {
        view: tableData.view,
        definition: tableData.definition,
        records,
        selection,
        selections: otherSelections.states,
        isTableBlockFocused,
        diff: tableData.diff,
    };

    const tableStatesContext = React.useContext(TableStatesContext);
    tableStatesContext.set(node.key, tableState);
    React.useEffect(() => {
        return () => {
            tableStatesContext.delete(node.key);
        };
    }, [tableStatesContext, node.key]);

    const viewSpec = viewSpecs[tableState.view.type];

    const createLayoutGroup = useBlockLayoutGroupFactory({ nodeKey: node.key });
    const palette = useTablePaletteFactory(node.key, {
        createExtraGroups: () => [createLayoutGroup()].filter(filterOutNullable),
    });

    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const paletteHandle = usePaletteHandle(buttonRef);
    const { tableIsScrollable } = useTableLayout(tableState.view);

    const isGridView = tableState.view.type === 'grid';

    const rowButtonPosition = React.useMemo(() => {
        if (!isGridView) return;

        if (tableIsScrollable) {
            return isTableBlockFocused
                ? '-bottom-7'
                : 'group-hover/table:-bottom-6 -bottom-[1.125rem]';
        }

        return isTableBlockFocused ? '-bottom-[1.675rem]' : '-bottom-[1.375rem]';
    }, [tableIsScrollable, isTableBlockFocused, isGridView]);

    return (
        <>
            <BlockWrapper
                {...props}
                palette={palette}
                contentProps={{
                    className: 'group/table',
                    style: isGridView
                        ? ({
                              '--block-highlight-inset': '0px 0.375rem -0.5rem -0.5rem',
                          } as React.CSSProperties)
                        : undefined,
                }}
            >
                <VoidBlockFrame
                    {...props}
                    selectable={viewSpec.selectableView}
                    isFocused={isTableBlockFocused}
                    selections={otherSelections.filtered}
                    onSelect={() => {
                        editor.change((change) => {
                            table.moveTableSelection(change, node, {});
                            change.focus();
                        });
                    }}
                    className={clsx('max-w-full', isGridView && 'w-fit')}
                >
                    <React.Suspense fallback={<BlockLoadingFallback />}>
                        <ToggleFragmentEditing readOnly={!!openedRecord}>
                            <viewSpec.ViewComponent
                                editor={props.editor}
                                nodeKey={node.key}
                                readOnly={readOnly}
                                openRecord={openRecord}
                                {...tableState}
                            />

                            {/* Add-column button (to the right of the table) */}
                            {!readOnly && isGridView ? (
                                <Tooltip label="Add column" position="bottom">
                                    <PlaceholderButton
                                        size="small"
                                        kind="ghost"
                                        label="Add column"
                                        icon={Icon.Plus}
                                        iconOnly
                                        className={clsx(
                                            'top-2 w-5 opacity-0 transition-all hover:opacity-100 group-hover/table:opacity-100',
                                            paletteHandle.opened && 'opacity-100',
                                            tableIsScrollable ? 'bottom-2' : 'bottom-0',
                                            isTableBlockFocused
                                                ? tableIsScrollable
                                                    ? '-right-[0.875rem]'
                                                    : '-right-4'
                                                : '-right-[0.675rem]'
                                        )}
                                        ref={buttonRef}
                                        onPress={() => {
                                            paletteHandle.open(
                                                createColumnDefsPalette((type) => {
                                                    const newCol = table.createColumnFromType(type);
                                                    editor.change((change) => {
                                                        table.insertColumn(
                                                            change,
                                                            node.key,
                                                            newCol
                                                        );
                                                    });
                                                })
                                            );
                                        }}
                                    />
                                </Tooltip>
                            ) : null}

                            {/* Add-row button (below the table) */}
                            {!readOnly && isGridView ? (
                                <Tooltip
                                    label="Add row"
                                    shortcut={shortcuts.TABLE_INSERT_ROW_BELOW}
                                    position="bottom"
                                >
                                    <PlaceholderButton
                                        size="small"
                                        kind="ghost"
                                        label="Add row"
                                        icon={Icon.Plus}
                                        iconOnly
                                        className={clsx(
                                            'left-0 h-5 opacity-0 transition-all hover:opacity-100 group-hover/table:opacity-100',
                                            tableIsScrollable ? 'right-2' : 'right-3',
                                            rowButtonPosition
                                        )}
                                        onPress={() => {
                                            editor.change((change) => {
                                                table.insertEmptyRecord(change, node.key);
                                            });
                                        }}
                                    />
                                </Tooltip>
                            ) : null}
                        </ToggleFragmentEditing>
                    </React.Suspense>
                </VoidBlockFrame>
            </BlockWrapper>
            {openedRecord ? (
                <RecordModal
                    editor={props.editor}
                    nodeKey={node.key}
                    readOnly={readOnly}
                    view={tableState.view}
                    records={tableState.records}
                    tableDefinition={tableState.definition}
                    record={openedRecord}
                    openRecord={openRecord}
                    onClose={() => {
                        setOpenedRecordKey(null);
                    }}
                />
            ) : null}
        </>
    );
}
